import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIdBadge,
  faStar,
  faMagnifyingGlassChart,
  faCrown,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "antd";
import confetti from "canvas-confetti";
import WelcomeModal from "Components/WelcomeModal";
import { useFeatureAccess } from "Hooks/useFeatureAccess";

const features = [
  {
    key: "minisite",
    title: "Create your FREE Minisite",
    description:
      "Create and manage your digital business profile. Edit and update your information easily.",
    icon: faIdBadge,
    path: "/digital-profile",
    color: "bg-gradient-to-br from-blue-500 to-blue-600",
    iconColor: "black",
    isFree: true,
  },
  {
    key: "review-generator",
    title: "Google Review AI Assistant",
    description:
      "Create ready-to-post Google reviews with AI and boost your business reputation.",
    icon: faStar,
    path: "/review-generator",
    color: "bg-gradient-to-br from-amber-500 to-amber-600",
    iconColor: "text-amber-500",
    isFree: false,
  },
  {
    key: "seo",
    title: "AI SEO",
    description: "Access AI SEO tools to improve your online visibility.",
    icon: faMagnifyingGlassChart,
    path: "/seo",
    color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    iconColor: "text-emerald-500",
    isFree: false,
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { hasAccess, userType, nextBillingAt, userData } = useFeatureAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const confettiIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (confettiIntervalRef.current) {
        clearInterval(confettiIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (searchParams.get("new_signup") === "true") {
      setShowWelcomeModal(true);

      // Fire confetti
      const duration = 15 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = {
        startVelocity: 30,
        spread: 360,
        ticks: 60,
        zIndex: 2000,
      };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(function () {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti(
          Object.assign({}, defaults, {
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          })
        );
        confetti(
          Object.assign({}, defaults, {
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          })
        );
      }, 250);

      confettiIntervalRef.current = interval;

      // Remove param from URL so it doesn't show again on reload
      setSearchParams((params) => {
        params.delete("new_signup");
        return params;
      });
    }
  }, [searchParams, setSearchParams]);

  const getTrialDaysLeft = () => {
    let targetDate = nextBillingAt;

    // Fallback for new accounts where nextBillingAt might be missing
    if (!targetDate && userData?.createdAt && userType === "trial") {
      const created = new Date(userData.createdAt);
      if (!isNaN(created.getTime())) {
        // Assume 7 day trial from creation
        targetDate = new Date(
          created.getTime() + 7 * 24 * 60 * 60 * 1000
        ).toISOString();
      }
    }

    if (!targetDate) return 0;
    const now = new Date();
    const end = new Date(targetDate);
    const diffTime = end - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysLeft = getTrialDaysLeft();

  const handleCardClick = (feature) => {
    // Always navigate to the feature page
    // The FeatureAccessWrapper will handle access control
    navigate(feature.path);
  };

  const getFeatureStatus = (feature) => {
    if (hasAccess(feature.key)) {
      return { accessible: true, reason: null };
    }

    return { accessible: false, reason: "premium" };
  };

  const renderBanner = () => {
    if (userType === "trial") {
      if (daysLeft > 0) {
        return (
          <div className="bg-green-600 text-white p-4 py-3 mb-8 squircle shadow-md flex flex-col sm:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-2 sm:mb-0">
              <div className="bg-white/20 p-2 squircle">
                <FontAwesomeIcon icon={faCrown} className="text-white" />
              </div>
              <div>
                <p className="font-bold text-lg">7-Day Free Trial Active</p>
                <p className="text-blue-100 text-sm">
                  You have full access to all premium features for {daysLeft}{" "}
                  more days.
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                window.open(
                  "https://wa.me/919426320180?text=Hey,%20I%20am%20enjoying%20free%20plan,%20i%20want%20to%20purchase%20plan",
                  "_blank"
                )
              }
              className="bg-white text-primary px-4 py-2 squircle font-bold text-sm">
              Purchase Plan Now
            </button>
          </div>
        );
      } else {
        return (
          <div className="bg-red-600 text-white px-4 py-3 mb-8 squircle hadow-md flex flex-col sm:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-2 sm:mb-0">
              <div className="bg-white/20 p-2 squircle">
                <FontAwesomeIcon icon={faStar} className="text-white" />
              </div>
              <div>
                <p className="font-bold text-lg">Free Trial Expired</p>
                <p className="text-red-100 text-sm">
                  Your trial has ended. Premium features are now locked.
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                window.open(
                  "https://wa.me/919426320180?text=Hey,%20my%20plan%20is%20expired%20,%20i%20want%20to%20purchase%20plan",
                  "_blank"
                )
              }
              className="bg-white text-red-600 px-4 py-2 squircle font-bold text-sm hover:bg-red-50 transition-colors">
              Contact Support to Unlock
            </button>
          </div>
        );
      }
    }

    if (userType === "free") {
      return (
        <div className="bg-gray-800 text-white px-4 py-3 mb-8 squircle shadow-md flex flex-col sm:flex-row items-center justify-between">
          <div className="flex items-center gap-3 mb-2 sm:mb-0">
            <div className="bg-white/20 p-2 squircle">
              <FontAwesomeIcon icon={faIdBadge} className="text-gray-300" />
            </div>
            <div>
              <p className="font-bold text-lg">Free Account</p>
              <p className="text-gray-300 text-sm">
                Upgrade to unlock premium features like Review Generator.
              </p>
            </div>
          </div>
          <button
            onClick={() =>
              window.open(
                "https://wa.me/919426320180?text=Hey,%20I%20want%20to%20purchase%20plan",
                "_blank"
              )
            }
            className="bg-white text-gray-900 px-4 py-2 squircle font-bold text-sm hover:bg-gray-100 transition-colors">
            Upgrade Plan
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen w-full bg-gray-100">
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
      />
      <div className="max-w-6xl mx-auto px-4 py-16">
        {renderBanner()}

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => {
            const status = getFeatureStatus(feature);

            return (
              <div
                key={feature.key}
                className="squircle relative bg-white shadow-md hover:shadow-lg transition-all duration-300 p-6 cursor-pointer hover:-translate-y-1"
                onClick={() => handleCardClick(feature)}>
                {/* Content */}
                <div className="relative">
                  {/* Pricing Badge */}
                  <div className="absolute top-4 right-4">
                    {feature.isFree ? (
                      <div className="flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full border-2 border-green-300 shadow-sm">
                        <span className="text-green-600">★</span>
                        <span>FREE</span>
                      </div>
                    ) : status.accessible && userType !== "free" ? (
                      <div className="flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full border-2 border-blue-300 shadow-sm">
                        <FontAwesomeIcon icon={faCrown} className="text-xs" />
                        <span>
                          {userType === "trial" ? `7 DAY FREE TRIAL` : "ACTIVE"}
                        </span>
                      </div>
                    ) : (
                      <Tooltip
                        title="Premium Feature: Click to learn more"
                        placement="right">
                        <div className="flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full border border-gray-200">
                          <FontAwesomeIcon icon={faCrown} className="text-xs" />
                          <span>PREMIUM</span>
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className={`${feature.color} squircle h-14 w-14 p-4 mb-6 inline-flex items-center justify-center`}>
                    <FontAwesomeIcon
                      icon={feature.icon}
                      className="text-white text-2xl"
                    />
                  </div>

                  {/* Title */}
                  <h3
                    className={`text-xl font-bold mb-3 leading-tight ${
                      feature.isFree ? "text-green-800" : "text-gray-900"
                    }`}>
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="leading-relaxed text-gray-600">
                    {feature.description}
                  </p>

                  {/* Action Button */}
                  <div className="mt-6">
                    <div
                      className={`inline-flex items-center text-sm font-medium transition-colors duration-200 ${
                        feature.isFree
                          ? "text-green-600 hover:text-green-700"
                          : "text-blue-600 hover:text-blue-700"
                      }`}>
                      {feature.isFree ? "Start Free" : "Get Started"}
                      <svg
                        className="ml-2 w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
